import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query"
import { createTRPCClient, httpBatchLink, httpLink } from "@trpc/client"
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query"
import superjson from "superjson"
import type { AppRouter } from "./server/routers/_app"
import {
  isForbiddenError,
  isTRPCClientError,
  isUnauthorizedError,
} from "./core/errors"

function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    return ""
  }
  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`
  }
  if (process.env.BASE_URL) {
    return process.env.BASE_URL
  }
  const port =
    process.env.PORT || (process.env.NODE_ENV === "test" ? "3001" : "3000")
  return `http://127.0.0.1:${port}`
}

const onError = (error: any) => {
  const err = error as any
  const code = err?.data?.code ?? err?.shape?.data?.code
  if (code === "UNAUTHORIZED") {
    const currentPath = window.location.pathname
    const loginPath = `/login?next=${encodeURIComponent(currentPath)}`
    window.location.href = loginPath
  }
}

// Singleton QueryClient for the entire app
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: (failureCount, error) => {
        if (isUnauthorizedError(error)) {
          return false
        }
        if (isForbiddenError(error)) {
          return false
        }
        if (
          isTRPCClientError(error) &&
          error.data?.code === "NO_GROUP_SELECTED"
        ) {
          return false
        }
        return failureCount < 2
      },
    },
  },
  queryCache: new QueryCache({
    onError,
  }),
  mutationCache: new MutationCache({
    onError,
  }),
})

export const trpcClient = createTRPCClient<AppRouter>({
  links: [
    process.env.NODE_ENV === "production"
      ? httpBatchLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
          maxItems: 4,
        })
      : httpLink({
          url: `${getBaseUrl()}/api/trpc`,
          transformer: superjson,
        }),
  ],
})

// tRPC options proxy for use with TanStack Query hooks
export const trpc = createTRPCOptionsProxy<AppRouter>({
  client: trpcClient,
  queryClient,
})
